import React, { ReactNode, useState } from 'react'
import { ClientUser } from '@numeo/types-core'
import { useAuth } from 'hooks/auth'
import { getTelegramBotUsername } from 'pages/dashboard/pages/updater-agent/pages/ongoing-loads/utils/helpers'
import { Button, Tooltip, Badge } from 'components/ds2'
import { TelegramGlyphIcon } from 'components/icons/TelegramGlyphIcon'
import { Send, MessageCircle, Copy, Pencil, Trash2, CheckCircle2, Info, Clock, ChevronDown, ChevronRight } from 'lucide-react'
import { useDriverTelegramGroup } from '../../hooks/useDriverTelegramGroup'
import { TelegramGroupModal } from './TelegramGroupModal'
import { TelegramSetupInstructions } from './TelegramSetupInstructions'
import { TelegramSetupStepItem } from './TelegramSetupStepItem'
import { buildTelegramConnectCommand } from '@/utils/telegram-connect-command'

/** The rounded driver-detail card shell (icon chip + title). Shared by the driver wizard's Personal step and
 *  the box-truck My Drivers detail, so both surfaces render the same visual system. */
export const Section = ({
    icon,
    title,
    children,
    className = '',
    badge,
}: {
    icon: ReactNode
    title: string
    children: ReactNode
    className?: string
    badge?: ReactNode
}) => (
    <div className={`rounded-2xl bg-surface p-4 shadow-surface ${className}`}>
        <div className="mb-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-soft text-accent">{icon}</span>
                <h4 className="text-base font-semibold text-foreground">{title}</h4>
            </div>
            {badge}
        </div>
        {children}
    </div>
)

/** A labelled field row (icon + label + control) inside a Section. */
export const FieldRow = ({ icon, label, required, children }: { icon?: ReactNode; label: string; required?: boolean; children: ReactNode }) => (
    <div className="min-w-0 space-y-1.5">
        <div className="flex items-center gap-1.5">
            {icon && <span className="flex items-center justify-center text-accent">{icon}</span>}
            <label className="text-xs font-medium text-foreground-muted">
                {label}
                {required && <span className="ml-0.5 text-danger">*</span>}
            </label>
        </div>
        <div className="min-w-0">{children}</div>
    </div>
)

/** The slice of a driver record the messaging panel needs — a full ClientUser (the wizard) or the box-truck
 *  claim record's DTO fields both satisfy it. `id` is the FLEET DRIVER record id (the /connect command's
 *  driver argument), not a standalone-account id. */
type MessagingServicesDriver = Pick<ClientUser, 'id' | 'telegramGroup' | 'whatsappNumber'>

interface MessagingServicesSectionProps {
    driver: MessagingServicesDriver
    /** The saved/removed Telegram group after an edit/delete — the caller patches its own driver state. */
    onTelegramGroupChange: (group: ClientUser['telegramGroup'] | undefined) => void
    /** Adds the product-tour data attributes (only the driver wizard runs that tour). */
    withTourTags?: boolean
    /** Expanded setup guidance in the Box Truck Add Driver form; other consumers keep the compact help. */
    showSetupInstructions?: boolean
    /**
     * Set when the caller's role may not change this driver: rename and unlink are disabled and this says why.
     * Passed by the Organization driver wizard; the Box Truck pages gate on their own access tier instead.
     */
    noAccessMessage?: string
}

/**
 * "Messaging Services" — the Telegram/WhatsApp connectivity card from the driver wizard's Personal step,
 * extracted so the box-truck My Drivers detail renders the IDENTICAL panel against its claim record (which
 * is a real fleet driver, so the /connect command + group linkage machinery works unchanged).
 */
export const MessagingServicesSection: React.FC<MessagingServicesSectionProps> = ({
    driver,
    onTelegramGroupChange,
    withTourTags = false,
    showSetupInstructions = false,
    noAccessMessage,
}) => {
    const { application } = useAuth()
    const [showTelegramHelp, setShowTelegramHelp] = useState(true)
    const [copiedField, setCopiedField] = useState<'command' | 'bot' | null>(null)
    const [editGroupName, setEditGroupName] = useState('')
    // Renaming or unlinking the group saves straight away, so it is off when the caller says the role may not edit.
    const canEditDrivers = !noAccessMessage
    const noAccessWrapper = canEditDrivers ? undefined : 'cursor-not-allowed *:pointer-events-none'

    const { username: telegramUsername, formattedTelUsername, isConfigured } = getTelegramBotUsername()
    const telegramLabel = driver.telegramGroup?.groupName || (driver.telegramGroup?.chatId ? 'Connected' : 'Not Connected')
    const whatsappLabel = driver.whatsappNumber || 'Coming soon'
    const telegramLink = telegramUsername ? `https://t.me/${telegramUsername.replace(/^@/, '')}` : ''
    const connectCommand = buildTelegramConnectCommand(application?.projectName, driver.id)

    const {
        editGroupName: handleEditGroupNameApi,
        deleteGroup: handleDeleteGroupApi,
        isEditing: isEditingGroupName,
        isDeleting: isDeletingGroup,
        isSaving: isSavingGroupName,
        setIsEditing: setIsEditingGroupName,
    } = useDriverTelegramGroup({ driver: driver as ClientUser })

    const handleCopy = async (value: string, field: 'command' | 'bot') => {
        if (!value) return
        try {
            await navigator.clipboard.writeText(value)
            setCopiedField(field)
            setTimeout(() => setCopiedField(null), 1500)
        } catch (error) {
            console.error('Failed to copy Telegram value:', error)
        }
    }

    const handleOpenEditGroupName = () => {
        if (!driver.telegramGroup?.chatId || !canEditDrivers) return
        setEditGroupName(driver.telegramGroup?.groupName || '')
        setIsEditingGroupName(true)
    }

    const handleSaveGroupName = async () => {
        try {
            const updated = await handleEditGroupNameApi(editGroupName)
            setIsEditingGroupName(false)
            if (updated?.telegramGroup) onTelegramGroupChange(updated.telegramGroup)
        } catch {
            // The hook already surfaces a snackbar for this error.
        }
    }

    const handleDeleteGroup = async () => {
        if (!canEditDrivers) return
        try {
            await handleDeleteGroupApi()
            onTelegramGroupChange(undefined)
        } catch {
            // The hook already surfaces a snackbar for this error.
        }
    }

    const renderConnectCommandButton = (withTour = false) => (
        <Tooltip
            content={copiedField === 'command' ? 'Copied!' : connectCommand ? `Copies "${connectCommand}"` : 'Save the driver first to generate the command'}
        >
            <span {...(withTour && withTourTags ? { 'data-tour': 'telegram-copy-command' } : {})}>
                <Button variant="ghost" size="sm" iconOnly onClick={() => handleCopy(connectCommand, 'command')} disabled={!connectCommand}>
                    <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                </Button>
            </span>
        </Tooltip>
    )

    return (
        <Section icon={<Send className="h-5 w-5" strokeWidth={1.5} />} title="Messaging Services">
            <div className="divide-y divide-border overflow-hidden rounded-xl bg-surface-secondary">
                <div className="flex items-center gap-3 px-3 py-2.5" {...(withTourTags ? { 'data-tour': 'telegram-connection' } : {})}>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft">
                        <TelegramGlyphIcon className="h-[18px] w-[18px] text-accent" />
                    </span>
                    <p className="w-24 shrink-0 text-sm font-medium text-foreground">Telegram</p>
                    <div className="flex min-w-0 flex-1 items-center gap-1">
                        {isConfigured && telegramLink ? (
                            <>
                                <a href={telegramLink} target="_blank" rel="noreferrer" className="truncate text-xs font-semibold text-accent hover:underline">
                                    {formattedTelUsername}
                                </a>
                                <Tooltip content={copiedField === 'bot' ? 'Copied!' : 'Copy bot name'}>
                                    <span>
                                        <Button variant="ghost" size="sm" iconOnly onClick={() => handleCopy(formattedTelUsername, 'bot')}>
                                            <Copy className="h-3 w-3" strokeWidth={1.5} />
                                        </Button>
                                    </span>
                                </Tooltip>
                                {driver.telegramGroup?.groupName && <p className="truncate text-sm text-foreground-muted">{driver.telegramGroup.groupName}</p>}
                            </>
                        ) : (
                            <p className="truncate text-sm text-foreground-muted">{telegramLabel}</p>
                        )}
                        {driver.telegramGroup?.chatId && (
                            <>
                                <Tooltip content={noAccessMessage ?? 'Edit'} wrapperClassName={noAccessWrapper}>
                                    <Button variant="ghost" size="sm" iconOnly onClick={handleOpenEditGroupName} disabled={!canEditDrivers}>
                                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                                    </Button>
                                </Tooltip>
                                <Tooltip content={noAccessMessage ?? 'Delete'} wrapperClassName={noAccessWrapper}>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        iconOnly
                                        onClick={handleDeleteGroup}
                                        disabled={isDeletingGroup || !canEditDrivers}
                                        className="text-danger"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                                    </Button>
                                </Tooltip>
                            </>
                        )}
                    </div>
                    {!showSetupInstructions && renderConnectCommandButton(true)}
                    <Badge
                        variant="soft"
                        type={driver.telegramGroup?.chatId ? 'success' : 'default'}
                        size="sm"
                        label={driver.telegramGroup?.chatId ? 'Connected' : 'Not Connected'}
                        prefix={<CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} />}
                    />
                </div>
                <div className="flex items-center gap-3 px-3 py-2.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-success-soft">
                        <MessageCircle className="h-[18px] w-[18px] text-success" strokeWidth={1.5} />
                    </span>
                    <p className="w-24 shrink-0 text-sm font-medium text-foreground">WhatsApp</p>
                    <p className="min-w-0 flex-1 truncate text-sm text-foreground-muted">{whatsappLabel}</p>
                    <Badge variant="soft" type="default" size="sm" label="Soon" prefix={<Clock className="h-3.5 w-3.5" strokeWidth={1.5} />} />
                </div>
            </div>

            {showSetupInstructions ? (
                <TelegramSetupInstructions
                    isConfigured={isConfigured}
                    telegramLink={telegramLink}
                    formattedTelUsername={formattedTelUsername}
                    connectCommand={connectCommand}
                    copiedField={copiedField}
                    withTourTags={withTourTags}
                    onCopy={handleCopy}
                />
            ) : isConfigured ? (
                <div className="mt-3 rounded-xl bg-surface-secondary">
                    <button
                        type="button"
                        onClick={() => setShowTelegramHelp(!showTelegramHelp)}
                        className="flex w-full items-center justify-between gap-2 p-3 text-left"
                    >
                        <span className="text-xs font-semibold uppercase tracking-wider text-foreground-muted">How to connect Telegram</span>
                        <span className="text-foreground-muted">
                            {showTelegramHelp ? <ChevronDown className="h-4 w-4" strokeWidth={1.5} /> : <ChevronRight className="h-4 w-4" strokeWidth={1.5} />}
                        </span>
                    </button>
                    {showTelegramHelp && (
                        <ol className="space-y-2.5 px-3 pb-3">
                            <TelegramSetupStepItem number={1} title="Add the bot to your group">
                                Copy <span className="font-semibold text-foreground">{formattedTelUsername}</span>, search it in Telegram, and add it to your
                                driver group.
                            </TelegramSetupStepItem>
                            <TelegramSetupStepItem number={2} title="Send the connect command">
                                <span className="flex items-center gap-1.5">
                                    <span>Copy the connect command and paste it as a message in that same group.</span>
                                    {renderConnectCommandButton()}
                                </span>
                            </TelegramSetupStepItem>
                            <TelegramSetupStepItem number={3} title="You’re connected">
                                The status above turns green “Connected” automatically — no further steps needed.
                            </TelegramSetupStepItem>
                        </ol>
                    )}
                </div>
            ) : null}

            {!showSetupInstructions && (
                <div className="mt-3 flex items-center gap-2 rounded-xl bg-accent-soft p-3">
                    <Info className="h-[18px] w-[18px] shrink-0 text-accent" strokeWidth={1.5} />
                    <p className="text-xs text-foreground">Connect using at least one platform to start communicating with your driver.</p>
                </div>
            )}

            <TelegramGroupModal
                open={isEditingGroupName}
                onClose={() => setIsEditingGroupName(false)}
                groupName={editGroupName}
                onGroupNameChange={setEditGroupName}
                onSave={handleSaveGroupName}
                isSaving={isSavingGroupName}
            />
        </Section>
    )
}
